// What the engine draws and plays at a time t of the video (spec E2): a pure function of the
// project and t, with no clock inside, so the preview, the browser's export and the Save page
// make the same frame at the same t. Times are milliseconds of the video.
import { fileMsAt } from "../speed-curve";
import { volumeAt } from "../timeline";
import type { CaptionLayer, EffectLayer, EngineProject, Layer, Sound, TextLayer, TransitionLayer } from "./project";

/** Whether a span shows at t: a time on a boundary belongs to the clip that starts there. */
export function within(span: { startMs: number; endMs: number }, atMs: number): boolean {
  return atMs >= span.startMs && atMs < span.endMs;
}

/** Where a picture held at its edge reads its file: this long before its end, its last frame. */
const LAST_FRAME_MS = 1;

/** Whether a picture shows at t: in its own span, or held at its edge by a transition. */
export function showsAt(layer: Layer, atMs: number): boolean {
  return within({ startMs: layer.shownFromMs, endMs: layer.shownToMs }, atMs);
}

/**
 * The time of its own file a picture shows at t, at its speed (spec clip-speed) or along its curve
 * (spec clip-speed-curves): its first frame before it, its last after.
 */
export function sourceMsAt(layer: Layer, atMs: number): number {
  if (layer.kind === "image") return 0;
  const held = Math.min(Math.max(atMs, layer.startMs), layer.endMs - LAST_FRAME_MS);
  return fileMsAt(layer, held);
}

/**
 * The pictures at t, bottom first, each with the time of its own file to show; `held` while a
 * transition shows it outside its own span (a video is paused there).
 */
export function layersAt(
  project: EngineProject,
  atMs: number,
): { layer: Layer; sourceMs: number; held: boolean }[] {
  return project.layers
    .filter((layer) => showsAt(layer, atMs))
    .map((layer) => ({ layer, sourceMs: sourceMsAt(layer, atMs), held: !within(layer, atMs) }));
}

/** How many pictures to come the preview loads at most, beside the ones showing (cut-preload). */
export const MOST_AHEAD = 3;

/**
 * The pictures that start showing in the `withinMs` after t, earliest first and at most
 * `MOST_AHEAD`, each with the time of its own file it shows first (its in-point, or its first
 * frame held by a transition): the preview loads them paused there, so a cut finds its next
 * picture decoded (spec cut-preload).
 */
export function layersAhead(
  project: EngineProject,
  atMs: number,
  withinMs: number,
): { layer: Layer; sourceMs: number }[] {
  return project.layers
    .filter((layer) => layer.shownFromMs > atMs && layer.shownFromMs <= atMs + withinMs)
    .sort((one, other) => one.shownFromMs - other.shownFromMs)
    .slice(0, MOST_AHEAD)
    .map((layer) => ({ layer, sourceMs: sourceMsAt(layer, layer.shownFromMs) }));
}

/** The transitions running at t, each with how far along it is, from 0 to 1. */
export function transitionsAt(
  project: EngineProject,
  atMs: number,
): { transition: TransitionLayer; progress: number }[] {
  return project.transitions
    .filter((transition) => within(transition, atMs))
    .map((transition) => ({
      transition,
      progress: (atMs - transition.startMs) / (transition.endMs - transition.startMs),
    }));
}

/** The texts at t, bottom first. */
/** The effects applied at t (#265), each with how far along its clip is, from 0 to 1. */
export function effectsAt(
  project: EngineProject,
  atMs: number,
): { effect: EffectLayer; progress: number }[] {
  return project.effects
    .filter((effect) => within(effect, atMs))
    .map((effect) => ({ effect, progress: (atMs - effect.startMs) / (effect.endMs - effect.startMs) }));
}

export function textsAt(project: EngineProject, atMs: number): TextLayer[] {
  return project.texts.filter((text) => within(text, atMs));
}

/** The captions showing at t (#239): from a caption's start up to its end, one rule for the
 * preview and the export (spec captions.md decision 15). */
export function captionsAt(project: EngineProject, atMs: number): CaptionLayer[] {
  return project.captions.filter((caption) => within(caption, atMs));
}

/**
 * A sound's level at t: its volume, or the line through its keyframes (spec volume-keyframes),
 * ramped up linearly over its fade-in from its start and down over its fade-out to its end
 * (ffmpeg's `afade`, which the server's render uses); 0 outside it.
 */
export function gainAt(sound: Sound, atMs: number): number {
  if (!within(sound, atMs)) return 0;
  const length = sound.endMs - sound.startMs;
  const rise = Math.min(sound.fadeInMs, length);
  const fall = Math.min(sound.fadeOutMs, length);
  const up = rise === 0 ? 1 : Math.min(1, (atMs - sound.startMs) / rise);
  const down = fall === 0 ? 1 : Math.min(1, (sound.endMs - atMs) / fall);
  return volumeAt(sound, atMs) * Math.max(0, Math.min(up, down));
}

/** The sounds heard at t, each with the time of its own file and its level then. */
export function soundsAt(
  project: EngineProject,
  atMs: number,
): { sound: Sound; sourceMs: number; gain: number }[] {
  return project.sounds
    .filter((sound) => within(sound, atMs))
    .map((sound) => ({
      sound,
      sourceMs: fileMsAt(sound, atMs),
      gain: gainAt(sound, atMs),
    }));
}

/** How many frames the video has at the project's rate: every frame that starts before its end. */
export function frameCount(project: EngineProject): number {
  return Math.ceil((project.durationMs * project.fps) / 1000);
}

/** The time of frame n, in milliseconds: n frames at the project's rate. */
export function frameTime(project: EngineProject, frame: number): number {
  return (frame * 1000) / project.fps;
}

/** The frame showing at t (the last one that starts at or before it), within the video. */
export function frameAt(project: EngineProject, atMs: number): number {
  const last = Math.max(0, frameCount(project) - 1);
  return Math.min(last, Math.max(0, Math.floor((atMs * project.fps) / 1000 + 1e-9)));
}
